import { ThreeEvent } from '@react-three/fiber';
import { DirectPointDragHandlers } from 'hooks/use-direct-point-drag';
import { useCallback, useRef } from 'react';
import { Vector3 } from 'three';
import {
  isLinesetPointDragUnchanged,
  LinesetLevelRange,
  LinesetPointDragUpdate,
  LineSetPointGeometry,
  resolveLinesetPointDrag
} from 'utils/3d-lineset-geometry';
import { LinesetPerimetersByLevelType } from 'utils/lineset-level-perimeters-types';
import { TransformControlsInteractionState } from 'utils/transform-controls';
import LinesetPoint3D from './lineset-point-3d';
import TranslateGizmo3D from './translate-gizmo-3d';

export type { LinesetPointDragUpdate } from 'utils/3d-lineset-geometry';

const EMPTY_PERIMETERS: LinesetPerimetersByLevelType = new Map();

type LinesetPointGizmoProps = {
  pointGeometry: LineSetPointGeometry;
  levelRanges?: LinesetLevelRange[];
  perimetersByLevel?: LinesetPerimetersByLevelType;
  opacity?: number;
  onDragStart: () => void;
  onDragChange: (update: LinesetPointDragUpdate) => void;
  onDragEnd: (update: LinesetPointDragUpdate | null) => void;
  onContextMenu?: (event: ThreeEvent<PointerEvent>) => void;
  dragHandlers?: DirectPointDragHandlers;
  onInteractionChange?: (state: TransformControlsInteractionState) => void;
};

export default function LinesetPointGizmo({
  pointGeometry,
  levelRanges = [],
  perimetersByLevel = EMPTY_PERIMETERS,
  opacity = 1,
  onDragStart,
  onDragChange,
  onDragEnd,
  onContextMenu,
  dragHandlers,
  onInteractionChange
}: LinesetPointGizmoProps) {
  const dragStartPointRef = useRef(pointGeometry.point);
  const dragStartLevelRef = useRef<string | undefined>(undefined);
  const pointGeometryRef = useRef(pointGeometry);
  const levelRangesRef = useRef(levelRanges);
  const perimetersByLevelRef = useRef(perimetersByLevel);

  pointGeometryRef.current = pointGeometry;
  levelRangesRef.current = levelRanges;
  perimetersByLevelRef.current = perimetersByLevel;

  const handleDragStart = useCallback(() => {
    dragStartPointRef.current = pointGeometryRef.current.point;
    dragStartLevelRef.current =
      levelRangesRef.current[
        pointGeometryRef.current.levelIndex
      ]?.levelUniqueId;
    onDragStart();
  }, [onDragStart]);

  const handleDragChange = useCallback(
    (anchorPosition: Vector3) => {
      onDragChange(
        resolveLinesetPointDrag(
          pointGeometryRef.current,
          anchorPosition,
          levelRangesRef.current,
          perimetersByLevelRef.current
        )
      );
    },
    [onDragChange]
  );

  const handleDragEnd = useCallback(
    (anchorPosition: Vector3 | null) => {
      if (!anchorPosition) {
        onDragEnd(null);
        return;
      }

      const update = resolveLinesetPointDrag(
        pointGeometryRef.current,
        anchorPosition,
        levelRangesRef.current,
        perimetersByLevelRef.current
      );
      const unchanged = isLinesetPointDragUnchanged(
        dragStartPointRef.current,
        update,
        dragStartLevelRef.current
      );

      onDragEnd(unchanged ? null : update);
    },
    [onDragEnd]
  );

  return (
    <TranslateGizmo3D
      position={[pointGeometry.x, pointGeometry.y, pointGeometry.z]}
      showY={!pointGeometry.heightLocked}
      onDragStart={handleDragStart}
      onDragChange={handleDragChange}
      onDragEnd={handleDragEnd}
      onInteractionChange={onInteractionChange}
    >
      <LinesetPoint3D
        opacity={opacity}
        selected
        onContextMenu={onContextMenu}
        dragHandlers={dragHandlers}
      />
    </TranslateGizmo3D>
  );
}
